// 시험 버전(A/B) 공용 — 시험 미리보기(ExamList · ExamDetailModal)와 채점 대시보드(GradingDashboard)가 같이 쓴다.
// 성취도 백엔드 규칙(2026-10-15 확인, docs/시험_관리_API.md 「시험 버전」): B형은 A형과 같은 문항·같은 배점이고 문항 단위로 순서만 섞인다.
// 버전이 있는지는 settings.generateABVersion 이 아니라 versions 의 키로 판단한다 — 설정만 바꾸는 업데이트는 versions 를 건드리지 않아
// 둘이 어긋날 수 있다. 답안은 배열 순서가 계약에 없으므로 questionId 로 문항에 맞춘다(docs/시험_답안_API.md 9절).
// 강사 화면과 같은 규칙이다(2026-10-15 강사 화면팀 통지에 맞춤).
import { normalizeGroupVideos } from './groupVideos'

const VERSION_ORDER = ['A', 'B']

export const normalizeVersionKey = (value) => String(value ?? '').trim().toUpperCase()

export const getQuestionId = (question) => {
  const id = question?.questionId || question?.id || question?._id || ''
  return id ? String(id) : ''
}

// 시험이 가진 버전 키 — versions 의 키 가운데 값이 객체인 것을 A·B 순으로(그 밖의 키는 이름순 뒤). 없으면 ['A']
export const getExamVersionKeys = (exam) => {
  const versions = exam?.versions
  if (!versions || typeof versions !== 'object') return ['A']
  const keys = Object.keys(versions).filter((key) => versions[key] && typeof versions[key] === 'object')
  if (keys.length === 0) return ['A']
  const rank = (key) => {
    const index = VERSION_ORDER.indexOf(key)
    return index === -1 ? VERSION_ORDER.length : index
  }
  return keys.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
}

// 한 버전의 스냅샷 문항을 그 버전의 순서로 — versions[버전].questionIds 를 먼저 따르고, 거기 없는 문항은 응답 순서대로 뒤에.
// 같은 ID 가 두 번 오면 처음 것만 쓴다. ID 가 없는 문항은 응답 순서대로 맨 뒤
export const orderVersionQuestions = (items, questionIds) => {
  const list = Array.isArray(items) ? items : []
  const byId = new Map()
  const withoutId = []
  list.forEach((question) => {
    const id = getQuestionId(question)
    if (!id) {
      withoutId.push(question)
      return
    }
    if (!byId.has(id)) byId.set(id, question)
  })
  const ordered = []
  const used = new Set()
  ;(Array.isArray(questionIds) ? questionIds : []).forEach((rawId) => {
    const id = rawId ? String(rawId) : ''
    if (!id || used.has(id) || !byId.has(id)) return
    used.add(id)
    ordered.push(byId.get(id))
  })
  byId.forEach((question, id) => {
    if (!used.has(id)) ordered.push(question)
  })
  return ordered.concat(withoutId)
}

// "그룹 보기" 판정 — questionGroupId 와 그룹 필드(지시사항·보기·지문·오디오·동영상) 가운데 하나라도 있는 문항이 있으면 그룹 블록으로 그린다
export const hasGroupInfo = (questions) =>
  (Array.isArray(questions) ? questions : []).some((item) =>
    item?.questionGroupId && (item.instruction || item.example || item.readingScript ||
      (item.groupAudio && (item.groupAudio.file || item.groupAudio.script)) ||
      normalizeGroupVideos(item.groupVideos).length > 0)
  )

// 한 버전의 순서에서 같은 그룹이 이어지는 구간마다 블록 하나 — B형은 한 그룹의 문항이 떨어져 있어 같은 그룹의 블록이 여러 개 생기고,
// 블록마다 지시사항·보기·지문·오디오·동영상을 다시 그린다. 문항 번호(number)는 버전 안에서 1부터 이어 센다(블록이 바뀌어도 다시 세지 않음).
// mapQuestion 은 스냅샷 문항을 화면용 문항으로 바꾸는 함수(그룹 필드는 스냅샷 원본에서 읽는다)
export const buildContiguousGroupBlocks = (questions, mapQuestion = (question) => question) => {
  const blocks = []
  let current = null
  ;(Array.isArray(questions) ? questions : []).forEach((question, index) => {
    const groupId = question?.questionGroupId || 'ungrouped'
    if (!current || current.groupId !== groupId) {
      current = {
        blockKey: `${groupId}:${index + 1}`,
        groupId,
        skill: question?.skill || question?.category || '',
        textBook: question?.textBook || '',
        instruction: question?.instruction || '',
        example: question?.example || '',
        readingScript: question?.readingScript || '',
        groupAudio: question?.groupAudio || null,
        // 그룹 동영상 — 2026-10-12 부터 스냅샷이 문항마다 담는다(항상 배열). 그 전 시험은 키가 없어 [] 로
        groupVideos: normalizeGroupVideos(question?.groupVideos),
        questions: [],
      }
      blocks.push(current)
    }
    current.questions.push({ ...mapQuestion(question), number: index + 1 })
  })
  return blocks
}

// 학생이 푼 버전의 스냅샷 — version 이 없거나 스냅샷에 그 키가 없으면 A형, A형도 없으면 예전 형식 exam.questions(versionKey: null)
export const resolveStudentSnapshot = (exam, version) => {
  const versions = exam?.versions && typeof exam.versions === 'object' ? exam.versions : {}
  const snapshotOf = (key) => {
    const list = versions?.[key]?.questions
    return Array.isArray(list) && list.length > 0 ? list : null
  }
  const key = normalizeVersionKey(version)
  if (key && snapshotOf(key)) return { versionKey: key, questions: snapshotOf(key) }
  if (snapshotOf('A')) return { versionKey: 'A', questions: snapshotOf('A') }
  return { versionKey: null, questions: Array.isArray(exam?.questions) ? exam.questions : [] }
}

// 문항별 분석의 기준 스냅샷 — A형(없으면 예전 형식 exam.questions)
export const getBaseQuestions = (exam) => resolveStudentSnapshot(exam, 'A').questions

// 답안을 questionId 로 그 버전 스냅샷에 맞춘 행 — 스냅샷 순서대로 한 행씩, 답안이 없는 문항은 answer: null(미응답).
// 스냅샷에 없는 questionId 의 답안(문항 삭제 등)과 questionId 가 없는 답안은 뒤에 이어 붙이고 question: null.
// 같은 questionId 의 답안이 두 번 오면 처음 것만 문항에 붙이고 나머지는 뒤에 둔다
export const buildStudentAnswerRows = (snapshotQuestions, answers) => {
  const answerById = new Map()
  const extras = []
  ;(Array.isArray(answers) ? answers : []).forEach((answer) => {
    const id = answer?.questionId ? String(answer.questionId) : ''
    if (!id || answerById.has(id)) {
      extras.push(answer)
      return
    }
    answerById.set(id, answer)
  })
  const rows = []
  const used = new Set()
  ;(Array.isArray(snapshotQuestions) ? snapshotQuestions : []).forEach((question, index) => {
    const id = getQuestionId(question)
    const answer = id && !used.has(id) ? answerById.get(id) || null : null
    if (answer) used.add(id)
    rows.push({ number: index + 1, questionId: id, question, answer })
  })
  let number = rows.length
  answerById.forEach((answer, id) => {
    if (used.has(id)) return
    number += 1
    rows.push({ number, questionId: id, question: null, answer })
  })
  extras.forEach((answer) => {
    number += 1
    rows.push({ number, questionId: answer?.questionId ? String(answer.questionId) : '', question: null, answer })
  })
  return rows
}

// 버전마다 questionId → 그 버전 안의 번호. 문항별 분석의 학생 줄에 「B형 7번」을 보일 때 쓴다
export const buildVersionNumberMaps = (exam) => {
  const maps = {}
  getExamVersionKeys(exam).forEach((key) => {
    const { versionKey, questions } = resolveStudentSnapshot(exam, key)
    if (versionKey !== key) return
    const map = new Map()
    questions.forEach((question, index) => {
      const id = getQuestionId(question)
      if (id && !map.has(id)) map.set(id, index + 1)
    })
    maps[key] = map
  })
  return maps
}

// 모든 학생의 답안을 questionId 로 모아 한 문항으로 센다 — 번호·내용·배점은 기준 스냅샷(A형) 순서, 스냅샷에 없는 questionId 는 뒤에 이어 붙인다.
// total 은 그 문항에 답안이 있는 학생 수, averageScore 는 그 답안 점수의 실제 평균.
// 답안이 하나도 없는 문항(total 0)은 목록에 두되 correctRate·averageScore 를 null 로 둔다 — 화면은 「-」로 보이고 막대·차트의 점을 비워
// 「응답 없음」과 「전원 오답 0%」를 가른다(2026-10-15 강사 화면팀 통지, 강사 화면과 같은 표시)
export const buildQuestionStats = (baseQuestions, students) => {
  const stats = new Map()
  const order = []
  const add = (id, number, question) => {
    stats.set(id, {
      id,
      questionId: id,
      number,
      question,
      maxScore: Number(question?.score) || 0,
      total: 0,
      correct: 0,
      scoreSum: 0,
    })
    order.push(id)
  }
  ;(Array.isArray(baseQuestions) ? baseQuestions : []).forEach((question) => {
    const id = getQuestionId(question)
    if (id && !stats.has(id)) add(id, order.length + 1, question)
  })
  ;(Array.isArray(students) ? students : []).forEach((student) => {
    ;(Array.isArray(student?.answers) ? student.answers : []).forEach((answer) => {
      const id = answer?.questionId ? String(answer.questionId) : ''
      if (!id) return
      if (!stats.has(id)) add(id, order.length + 1, null)
      const stat = stats.get(id)
      stat.total += 1
      if (answer.isCorrect === true) stat.correct += 1
      stat.scoreSum += Number(answer.score) || 0
    })
  })
  return order.map((id) => {
    const stat = stats.get(id)
    return {
      ...stat,
      incorrect: stat.total - stat.correct,
      correctRate: stat.total > 0 ? Math.round((stat.correct / stat.total) * 100) : null,
      averageScore: stat.total > 0 ? stat.scoreSum / stat.total : null,
    }
  })
}
